import type { ReactNode } from 'react';
import { isMissingRoute, isNotStaff, isUnavailable } from '../api';
import type { AsyncState } from './async';
import { go, href, type Route } from './route';

/**
 * An internal link that is a real link.
 *
 * A real `<a href>` with a real path, so middle-click, cmd-click and "copy link
 * address" all work — the same reasoning that put the route in the URL in the
 * first place. The handler only suppresses the full page reload, and only for a
 * plain left click.
 */
export function Link({
  to,
  children,
  className,
  title,
}: {
  to: Route;
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <a
      href={href(to)}
      className={className}
      title={title}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        go(to);
      }}
    >
      {children}
    </a>
  );
}

/* --- the four states every view has --------------------------------------
   Loading, empty, "nothing matched" and "the call failed" are four different
   facts and they get four different renderings. The one that matters most is
   the last pair: an operator staring at an empty table has to be able to tell
   "we looked and there is nothing" from "we never found out", because the first
   is a calm answer and the second is an incident. They used to be one grey
   line of text apart. */

export function Loading({ what }: { what?: string }) {
  return <p className="sub">loading{what ? ` ${what}` : ''}…</p>;
}

export function Problem({
  error,
  what,
  retry,
}: {
  error: string;
  what?: string;
  retry?: () => void;
}) {
  return (
    <div className="notice notice-bad">
      <b>Could not load {what ?? 'this'}.</b>
      <span className="mono small">{error}</span>
      <span className="muted small">
        Nothing has been ruled out — this is a failed call, not an empty result.
      </span>
      {retry && <button onClick={retry}>Try again</button>}
    </div>
  );
}

export function Empty({
  what,
  filtered,
  onClear,
}: {
  what: string;
  filtered?: boolean;
  onClear?: () => void;
}) {
  if (filtered) {
    return (
      <div className="notice">
        <b>No {what} matched this filter.</b>
        <span className="muted small">
          The call succeeded. Rows came back; none of them matched what you asked
          for.
        </span>
        {onClear && (
          <button className="link" onClick={onClear}>
            clear the filter
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="notice">
      <b>No {what}.</b>
      <span className="muted small">The call succeeded and came back empty.</span>
    </div>
  );
}

/**
 * Something this portal has not built, said out loud.
 *
 * The iPhone app's rule, applied here: a row that says "not built yet" beats one
 * that silently does nothing. An operator who presses a control and sees no
 * change cannot tell a missing feature from a broken one, and will spend the
 * next twenty minutes finding out which.
 */
export function NotBuilt({ what, detail }: { what: string; detail?: ReactNode }) {
  return (
    <div className="notice stub">
      <b>{what} — not built yet.</b>
      {detail && <span className="muted small">{detail}</span>}
    </div>
  );
}

/**
 * Deployed, answered, and never going to work here.
 *
 * The state the Azure port needed. Two backend seams refuse permanently on this
 * deployment — shared/workflow.py has no pipeline introspection, shared/
 * directory.py has no user directory — and every screen over them was rendering
 * a red, retryable "the call failed" box for a condition that no amount of
 * retrying changes. That is worse than useless on the Pipeline screen, which is
 * the one an operator opens during an incident: it reads as "the console is
 * broken too" when the truth is "this view does not exist on this lane".
 *
 * `reason` is the SERVER'S OWN SENTENCE, printed verbatim. Both handlers answer
 * with a full explanation ("pipeline introspection is not available on this
 * deployment", "FAMILY_USER_POOL_ID not configured"), and paraphrasing it here
 * would put a second, staler description of the cause in a second repository.
 *
 * NO RETRY BUTTON, deliberately. A button that cannot succeed is a minute of
 * somebody's incident.
 */
export function Unavailable({
  what,
  reason,
  detail,
}: {
  what: string;
  reason: string;
  detail?: ReactNode;
}) {
  return (
    <div className="notice stub">
      <b>{what} — not available on this deployment.</b>
      <span className="mono small">{reason}</span>
      {detail && <span className="muted small">{detail}</span>}
      <span className="muted small">
        This is an answer, not a failure: the call reached the API and the API
        said it cannot serve this. Retrying will return the same thing.
      </span>
    </div>
  );
}

/**
 * Signed in, wrong role. Rendered instead of an alarm because nothing is broken.
 *
 * Normally caught at boot (App.tsx probes /admin/health once), so reaching this
 * means the role was removed mid-session — or that Entra issued a token before
 * an assignment change caught up. Either way the fix is the same sentence, so
 * it is written once here.
 *
 * Every path reaches it through `Failed` (a load) or `Refused` (a change), plus
 * App.tsx's boot probe, which renders this component directly. It used to be
 * two of those: Ledger.tsx had no error layer at all and Families.tsx called
 * `Problem` by hand, so on those two screens — the two that hold the money — a
 * mid-session role removal rendered as a red retryable box or, on Ledger, as
 * the bare string "404".
 */
export function NotStaff({ reason }: { reason: string }) {
  return (
    <div className="notice notice-bad">
      <b>This account is not in the staff role.</b>
      <span className="mono small">{reason}</span>
      <span className="muted small">
        The token is genuine and the tenant is right — the account simply is not
        assigned. Fix it by assigning the <span className="mono">staff</span> app
        role on the showling-staff enterprise application; the last step of{' '}
        <span className="mono">azure/entra_bootstrap.sh</span> does exactly that.
        Signing out and in again will not change it.
      </span>
    </div>
  );
}

/**
 * Render one async value, with the right state for each outcome.
 *
 * FOUR ways a call can fail, and they get four renderings, because an operator
 * has to be able to act on the difference:
 *
 *  - `missing`  — the route is not deployed. A stub, not an alarm.
 *                 `isMissingRoute` decides, off the handler's literal "unknown
 *                 admin route" body or a 404 the Functions host answered itself.
 *  - unavailable — the route IS deployed and says it cannot serve this here.
 *                 Permanent, so no retry. Always shown, with no opt-in prop:
 *                 an unhandled 503 rendering as a red retryable box was the
 *                 exact bug the Azure port had to fix on five screens, and
 *                 making it opt-in is how it would come back.
 *  - not staff  — a role problem wearing a call's clothing. Never an alarm.
 *  - anything else — a genuine failure, with Try again.
 *
 * `unavailableDetail` is the screen's chance to add what the server cannot know
 * (which seam, what would have to be built). The server's own sentence is shown
 * either way.
 */
export function Result<T>({
  of,
  what,
  retry,
  missing,
  unavailableDetail,
  children,
}: {
  of: AsyncState<T>;
  what?: string;
  retry?: () => void;
  missing?: ReactNode;
  unavailableDetail?: ReactNode;
  children: (value: T) => ReactNode;
}) {
  if (of.state === 'loading') return <Loading what={what} />;
  if (of.state === 'error') {
    return (
      <Failed
        what={what}
        error={of.error}
        cause={of.cause}
        retry={retry}
        missing={missing}
        unavailableDetail={unavailableDetail}
      />
    );
  }
  return <>{children(of.value)}</>;
}

/**
 * The triage, on its own, for callers that are not a `Result`.
 *
 * The dashboard settles six sources independently rather than rendering one
 * async value, so it cannot use `Result` — and before this existed it hand-rolled
 * `<Problem>` for each of them, which is how a permanent 503 came to be shown
 * with a Try again button on the busiest screen in the console. One function
 * decides what a failure means, and both callers use it.
 */
export function Failed({
  what,
  error,
  cause,
  retry,
  missing,
  unavailableDetail,
}: {
  what?: string;
  error: string;
  cause: unknown;
  retry?: () => void;
  missing?: ReactNode;
  unavailableDetail?: ReactNode;
}) {
  if (isNotStaff(cause)) return <NotStaff reason={error} />;
  if (isUnavailable(cause)) {
    return (
      <Unavailable
        what={what ? cap(what) : 'This view'}
        reason={error}
        detail={unavailableDetail}
      />
    );
  }
  if (missing !== undefined && isMissingRoute(cause)) {
    return <NotBuilt what={what ? cap(what) : 'This view'} detail={missing} />;
  }
  return <Problem error={error} what={what} retry={retry} />;
}

/**
 * A CHANGE the server would not make. The other half of `Failed`, and the half
 * that did not exist.
 *
 * `Problem` is written for a read: "Could not load this… Nothing has been ruled
 * out — this is a failed call, not an empty result." Every word of that is
 * wrong about a refused mutation, and Families.tsx rendered it for one. Type 0
 * into the credits box with a reason, press Apply, and the server correctly
 * answers 400 "credits must be non-zero" — at which point the whole detail
 * panel was replaced by a red box telling the operator that nothing had been
 * ruled out, the ledger and videos and commissions they were reading vanished,
 * and the reason they had typed went with them. Recovery was closing the family
 * and opening it again.
 *
 * So: the discriminators still run — a role removed mid-session and a route
 * that answers 501/503 mean the same thing whether you were reading or writing
 * — and the fallback says the one thing an operator needs to know about a
 * refused change, which is that it did not happen.
 *
 * NO RETRY BUTTON. A retry here would re-POST, and the caller's own control is
 * still on screen with the values still in it. Retrying a write from an error
 * box is how a credit grant gets applied twice.
 */
export function Refused({
  what,
  error,
  cause,
}: {
  what: string;
  error: string;
  cause: unknown;
}) {
  if (isNotStaff(cause)) return <NotStaff reason={error} />;
  if (isUnavailable(cause)) return <Unavailable what={cap(what)} reason={error} />;
  if (isMissingRoute(cause)) {
    return (
      <NotBuilt
        what={cap(what)}
        detail="The route answered nothing at all — this build of the staff API does not have it."
      />
    );
  }
  return (
    <div className="notice notice-bad">
      <b>{cap(what)} was refused.</b>
      <span className="mono small">{error}</span>
      <span className="muted small">
        Nothing was applied. Everything on this screen is exactly as it was, and
        what you typed is still in the form — correct it and try again.
      </span>
    </div>
  );
}

function cap(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/* --- small pieces --------------------------------------------------------- */

export type Tone = 'on' | 'warn' | 'bad' | 'run' | 'plain';

export function Pill({
  tone = 'plain',
  title,
  children,
}: {
  tone?: Tone;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span className={`pill${tone === 'plain' ? '' : ` pill-${tone}`}`} title={title}>
      {children}
    </span>
  );
}

/**
 * A status word, coloured the same way everywhere.
 *
 * Covers both vocabularies on purpose — DynamoDB's (`generating`,
 * `awaiting_approval`, `preview_ready`) and Step Functions' (`RUNNING`,
 * `SUCCEEDED`) — because a commission detail screen shows both at once, and
 * they demonstrably disagree: the one failed episode in production is `failed`
 * in DynamoDB and `SUCCEEDED` in Step Functions, because the catcher ran and
 * returned cleanly. Colouring them by the same rules is what makes that
 * disagreement legible instead of confusing.
 */
export function StatusPill({ status }: { status?: string }) {
  if (!status) return <span className="muted">—</span>;
  const key = status.toLowerCase();
  const tone: Tone =
    key === 'failed' || key === 'timed_out' || key === 'aborted'
      ? 'bad'
      : key === 'running' || key === 'generating' || key === 'previewing'
        ? 'run'
        : key === 'awaiting_approval' || key === 'pending_redrive'
          ? 'warn'
          : key === 'succeeded' || key === 'published' || key === 'preview_ready'
            ? 'on'
            : 'plain';
  return <Pill tone={tone}>{status}</Pill>;
}

export function Card({
  label,
  value,
  note,
  tone,
  onClick,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  tone?: 'warn' | 'bad';
  onClick?: () => void;
}) {
  const className =
    'card' + (tone === 'bad' ? ' card-bad' : tone === 'warn' ? ' card-warn' : '');
  return (
    <div
      className={className}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <span className="card-label">{label}</span>
      <span className={`card-value${tone === 'bad' ? ' neg' : tone === 'warn' ? ' warn' : ''}`}>
        {value}
      </span>
      {note && <span className="card-note">{note}</span>}
    </div>
  );
}

/** A label/value grid for detail screens. Empty values are rendered as an
 *  explicit dash — a blank cell reads as "loading" or "bug", not as "none". */
export function Facts({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="facts">
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: 'contents' }}>
          <dt>{label}</dt>
          <dd>{value === undefined || value === null || value === '' ? <span className="muted">—</span> : value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="toolbar">{children}</div>;
}

/** A truncated identifier that keeps the whole thing on hover and one click
 *  from the clipboard. Operators paste these into database queries and support
 *  threads all day; a shortened id you cannot recover costs a scan to find. */
export function Id({ value, chars = 8 }: { value?: string | null; chars?: number }) {
  if (!value) return <span className="muted">—</span>;
  const short = value.length > chars ? value.slice(0, chars) : value;
  return (
    <code
      className="small nowrap"
      title={`${value} — click to copy`}
      style={{ cursor: 'copy' }}
      onClick={() => void navigator.clipboard?.writeText(value)}
    >
      {short}
    </code>
  );
}

/**
 * A filter that was applied in the browser rather than by the API.
 *
 * Said out loud every time, because the failure mode is silent and specific:
 * filtering a single page here means "the family's rows within this page", not
 * "the family's rows". An operator who reads it as the second concludes a family
 * has no videos when the API simply had not reached them yet. `veo-test-2` — a
 * live commission row that no index returns — is the standing reminder that the
 * row you cannot see is the one that matters.
 */
export function LocalFilterNote({
  active,
  what,
  why,
}: {
  active: boolean;
  what: string;
  why: string;
}) {
  if (!active) return null;
  return (
    <div className="notice notice-warn">
      <b>Filtered in this browser, not by the API.</b>
      <span className="muted small">
        {why} So this is the matching {what} <em>on this page</em> — not all of
        them. Page forward to keep looking.
      </span>
    </div>
  );
}

/* --- formatting ----------------------------------------------------------- */

/** ISO timestamp -> something a human reads at a glance. */
export function when(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function span(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
}

export function ago(value?: string): string {
  const at = ms(value);
  if (at === null) return '';
  const seconds = (Date.now() - at) / 1000;
  return seconds < 0 ? 'just now' : `${span(seconds)} ago`;
}

/** Wall time between two instants, or from `from` until now when `to` is
 *  absent — which is exactly the "how long has this been running" case. */
export function elapsed(from?: string, to?: string): string {
  const start = ms(from);
  if (start === null) return '';
  const end = ms(to) ?? Date.now();
  return span((end - start) / 1000);
}

export function ms(value?: string): number | null {
  if (!value) return null;
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : at;
}

export function money(value?: number | null): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  // Four places, not two: a single 8-second clip costs fractions of a cent and
  // rounding it to $0.00 is how per-shot spend stopped being watchable.
  return value >= 1 ? `$${value.toFixed(2)}` : `$${value.toFixed(4)}`;
}
